import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePark } from '../contexts/ParkContext';
import {
  LayoutDashboard,
  Map,
  Calculator,
  DollarSign,
  Receipt,
  BarChart3,
  Users,
  Building2,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Settings,
  Crown,
  Shield,
  BookUser,
  Droplets,
  Truck
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Button } from './ui/button';

const Layout = ({ children }) => {
  const { user, logout, isPropietario, isMaster, canEditData, canManageUsers } = useAuth();
  const { parks, selectedPark, setSelectedPark, selectedYear, setSelectedYear } = usePark();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  // Role labels for display
  const roleLabels = {
    propietario: 'Propietario',
    master: 'Master',
    admin: 'Administrador',
    consulta: 'Consulta',
    proveedor: 'Proveedor'
  };

  // Navigation items based on role
  const getNavItems = () => {
    const items = [];
    
    // Propietario: Only sees user management (masters)
    if (isPropietario()) {
      items.push({ to: '/usuarios', icon: Crown, label: 'Usuarios Master' });
      return items;
    }
    
    // Master and Admin: Full operational access
    if (canEditData()) {
      items.push(
        { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { to: '/lotes', icon: Map, label: 'Directorio de Lotes' },
        { to: '/directorio', icon: BookUser, label: 'Directorio de Personas' },
        { to: '/presupuesto', icon: Calculator, label: 'Presupuesto' },
        { to: '/cuotas', icon: DollarSign, label: 'Cuotas e Ingresos' },
        { to: '/agua', icon: Droplets, label: 'Agua' },
        { to: '/gastos', icon: Receipt, label: 'Gastos' },
        { to: '/proveedores', icon: Truck, label: 'Proveedores' },
        { to: '/reportes', icon: BarChart3, label: 'Reportes' }
      );
    }
    
    // Master: Can also manage users
    if (canManageUsers() && !isPropietario()) {
      items.push({ to: '/usuarios', icon: Users, label: 'Usuarios' });
    }
    
    // Master and Admin: Settings
    if (canEditData()) {
      items.push({ to: '/configuraciones', icon: Settings, label: 'Configuraciones' });
    }
    
    // Consulta: Limited access
    if (user?.rol === 'consulta') {
      items.push(
        { to: '/mi-cuenta', icon: LayoutDashboard, label: 'Mi Estado de Cuenta' },
        { to: '/reportes', icon: BarChart3, label: 'Reportes' }
      );
    }
    
    return items;
  };

  const navItems = getNavItems();

  return (
    <div className="app-layout">
      {/* Mobile menu button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-30 p-2 bg-white border border-slate-200 shadow-sm"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="mobile-menu-btn"
      >
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Sidebar */}
      <aside className={`app-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="app-logo" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <img src="/naave-logo.png" alt="NAAVE" style={{ height: '50px' }} data-testid="app-logo" />
        </div>

        {/* Park & Year Selector - Only for master and admin who work with parks */}
        {canEditData() && !isPropietario() && parks.length > 0 && (
          <div className="px-4 py-4 border-b border-slate-200 space-y-3">
            <Select
              value={selectedPark?.id || ''}
              onValueChange={(value) => {
                const park = parks.find(p => p.id === value);
                setSelectedPark(park);
              }}
            >
              <SelectTrigger className="w-full input-industrial" data-testid="park-selector">
                <Building2 size={16} className="mr-2 text-slate-400" />
                <SelectValue placeholder="Seleccionar parque" />
              </SelectTrigger>
              <SelectContent>
                {parks.map(park => (
                  <SelectItem key={park.id} value={park.id}>
                    {park.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={selectedYear.toString()}
              onValueChange={(value) => setSelectedYear(parseInt(value))}
            >
              <SelectTrigger className="w-full input-industrial" data-testid="year-selector">
                <SelectValue placeholder="Año" />
              </SelectTrigger>
              <SelectContent>
                {years.map(year => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Navigation */}
        <nav className="nav-section flex-1 overflow-y-auto">
          <span className="nav-label">Menú</span>
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `sidebar-nav-item ${isActive ? 'active' : ''}`
              }
              onClick={() => setSidebarOpen(false)}
              data-testid={`nav-${item.to.replace('/', '')}`}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User Menu */}
        <div className="user-menu">
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 flex items-center justify-center font-bold text-sm ${
              isPropietario() ? 'bg-amber-500 text-white' :
              isMaster() ? 'bg-blue-600 text-white' :
              'bg-slate-900 text-white'
            }`}>
              {isPropietario() ? <Crown size={18} /> :
               isMaster() ? <Shield size={18} /> :
               user?.nombre?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm text-slate-900 truncate">{user?.nombre}</p>
              <p className="text-xs text-slate-500">{roleLabels[user?.rol] || user?.rol}</p>
            </div>
          </div>
          <Button
            variant="outline"
            className="w-full justify-start gap-2 rounded-none"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut size={16} />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="app-main">
        <div className="app-content">
          {children}
        </div>
      </main>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-10 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;
